import React, { useState, useEffect } from 'react';
import './TaskDetailDrawer.css';

export default function TaskDetailDrawer({ task, onClose, onSave, onRefine }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [acceptanceCriteria, setAcceptanceCriteria] = useState([]);
  const [refineInstruction, setRefineInstruction] = useState('');
  const [refining, setRefining] = useState(false);
  const [refineError, setRefineError] = useState(null);

  useEffect(() => {
    if (task) {
      setTitle(task.title || '');
      setDescription(task.description || '');
      setAcceptanceCriteria(Array.isArray(task.acceptance_criteria) ? [...task.acceptance_criteria] : []);
      setRefineInstruction('');
      setRefineError(null);
    }
  }, [task]);

  if (!task) return null;

  const handleSaveEdits = () => {
    onSave({
      title: title.trim(),
      description: description.trim(),
      acceptance_criteria: acceptanceCriteria.filter(Boolean)
    });
  };

  const handleRefineSubmit = async () => {
    if (!refineInstruction.trim() || !onRefine) return;
    setRefining(true);
    setRefineError(null);
    try {
      await onRefine(refineInstruction.trim(), task.temp_id);
      setRefineInstruction('');
    } catch (err) {
      setRefineError(err.response?.data?.error || err.message || 'Refinement failed');
    } finally {
      setRefining(false);
    }
  };

  const addCriterion = () => setAcceptanceCriteria((prev) => [...prev, '']);
  const updateCriterion = (index, value) => {
    setAcceptanceCriteria((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };
  const removeCriterion = (index) => {
    setAcceptanceCriteria((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="fds-drawer-backdrop" onClick={onClose} role="presentation">
      <div className="fds-drawer-panel" onClick={(e) => e.stopPropagation()}>
        <div className="fds-drawer-header">
          <h3 className="fds-drawer-title">Task details</h3>
          <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
        </div>
        <div className="fds-drawer-body">
          <p className="fds-drawer-meta">
            <span className="fds-badge">{task.temp_id}</span> {task.type}
          </p>

          <div className="fds-drawer-section">
            <label className="form-label">Title</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="fds-drawer-section">
            <label className="form-label">Description</label>
            <textarea
              className="form-control form-control-sm"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="fds-drawer-section">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <label className="form-label mb-0">Acceptance criteria</label>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addCriterion}>
                Add
              </button>
            </div>
            <ul className="fds-criteria-list">
              {acceptanceCriteria.map((c, i) => (
                <li key={i} className="fds-criterion-item">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={c}
                    onChange={(e) => updateCriterion(i, e.target.value)}
                    placeholder="Criterion"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => removeCriterion(i)}
                    aria-label="Remove"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {task.dependencies && task.dependencies.length > 0 && (
            <div className="fds-drawer-section">
              <h4>Dependencies</h4>
              <p>{task.dependencies.join(', ')}</p>
            </div>
          )}
          {task.children && task.children.length > 0 && (
            <div className="fds-drawer-section">
              <h4>Children</h4>
              <p>{task.children.join(', ')}</p>
            </div>
          )}

          <div className="fds-drawer-section">
            <button type="button" className="btn btn-primary btn-sm" onClick={handleSaveEdits}>
              Save changes
            </button>
          </div>

          {onRefine && (
            <div className="fds-drawer-section fds-refine-section">
              <h4>Refine with AI</h4>
              <textarea
                className="form-control form-control-sm mb-2"
                rows={3}
                placeholder="e.g. Add a criterion for error handling, or split this into two tasks..."
                value={refineInstruction}
                onChange={(e) => setRefineInstruction(e.target.value)}
                disabled={refining}
              />
              {refineError && (
                <div className="alert alert-danger py-2 mb-2" role="alert">
                  {refineError}
                </div>
              )}
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={handleRefineSubmit}
                disabled={refining || !refineInstruction.trim()}
              >
                {refining ? 'Refining…' : 'Refine with AI'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
